/* eslint-disable class-methods-use-this */
import { makeAutoObservable } from 'mobx';
import apolloClient from 'api/apolloClient';
import { ApolloQueryResult } from '@apollo/client';
import {
  FINISH_ENROLL,
  GET_ENROLLS_BY_USER_ID,
  GET_INVITED_SUBSCRIBERS,
  REMIND_FINISH_SE,
  REMOVE_SUBSCRIBER,
  UPDATE_EMPLOYEE_CLASSES,
  SUBMIT_ELECTION,
  UPDATE_PRODUCT_CLASSES,
  GET_EMPLOYEE_CLASSES_WITH_EE,
  GET_SUBS_VALIDATIONS,
} from 'api/queries/electionService';

import {
  SubscriberSERoles,
  ElectionStatus,
  Subscriber,
  EeClass,
  SubscriberAddOnMethod,
  SubscriberProgress,
  StatisticsValues,
  EnrollsUsers,
  ProductsInClass,
  CompleteElectionsCheck,
  EstimatedCostStatistics,
} from 'types/electionService';

export const INITIAL_SUBSCRIBER: Subscriber = {
  id: '',
  periodId: '',
  groupId: '',
  ssn: '',
  email: '',
  phone: '',
  employeeClass: '',
  selfEnrollStatus: null,
  subSERole: 'EMPLOYEE' as SubscriberSERoles,
  userId: null,
  seInviteDate: '',
  choice: [],
  common: {
    firstName: '',
    lastName: '',
  },
  custom: {
    dependents: [],
  },
  electionStatus: 'ENROLLED' as ElectionStatus,
  productLineToCarrier: {},
  history: [],
  addOnDate: '',
  addOnMethod: SubscriberAddOnMethod.LINK,
  progress: SubscriberProgress.NOT_YET_STARTED,
  tasks: [],
  interestedProductLines: [],
};

export const INITIAL_STATISTIC: StatisticsValues = {
  total: 0,
  elected: 0,
  electionsMissing: 0,
  waived: 0,
  invited: 0,
  registered: 0,
  estimatedCost: {} as EstimatedCostStatistics,
};

export const INITIAL_COMPLETE_ELECTION_CHECK = {
  allWithClasses: false,
  subsWithClass: 0,
  isProductLinesQuestionAnswered: true,
  isWaiverQuestionAnswered: true,
  isDependentOnProductLineValid: true,
  subsWithoutElection: 0,
  generalQuestionAnswered: true,
};

export class ElectionStore {
  // eslint-disable-next-line class-methods-use-this
  async removeSubscriber(id: string): Promise<void> {
    await apolloClient.mutate<{ removeSubscriber: string }>({
      mutation: REMOVE_SUBSCRIBER,
      variables: { id },
    });
  }

  // eslint-disable-next-line class-methods-use-this
  async getInvitedSubscribers(periodId: string): Promise<ApolloQueryResult<{ getInvitedSubscribers: Subscriber[] }>> {
    return apolloClient.query<{ getInvitedSubscribers: Subscriber[] }>({
      query: GET_INVITED_SUBSCRIBERS,
      fetchPolicy: 'no-cache',
      variables: { periodId },
    });
  }

  // eslint-disable-next-line class-methods-use-this
  async submitElection(groupId: string, periodId: string): Promise<void> {
    await apolloClient.mutate({
      mutation: SUBMIT_ELECTION,
      variables: { groupId, periodId },
    });
  }

  // eslint-disable-next-line class-methods-use-this
  getEnrollsByUserId(): Promise<ApolloQueryResult<{ getEnrollsByUserId: EnrollsUsers }>> {
    return apolloClient.query<{ getEnrollsByUserId: EnrollsUsers }>({
      query: GET_ENROLLS_BY_USER_ID,
    });
  }

  // eslint-disable-next-line class-methods-use-this
  async finishEnroll(subId: string): Promise<void> {
    await apolloClient.mutate({
      mutation: FINISH_ENROLL,
      variables: { subId },
    });
  }

  // eslint-disable-next-line class-methods-use-this
  async remindFinishSE(periodId: string, subIds: string[]): Promise<void> {
    await apolloClient.query({
      query: REMIND_FINISH_SE,
      variables: { periodId, subIds },
    });
  }

  // eslint-disable-next-line class-methods-use-this
  getEmployeeClassesWithEe(
    groupId: string,
    periodId: string,
  ): Promise<ApolloQueryResult<{ getEmployeeClassesWithEe: EeClass[] }>> {
    return apolloClient.query({
      query: GET_EMPLOYEE_CLASSES_WITH_EE,
      variables: { groupId, periodId },
      fetchPolicy: 'no-cache',
    });
  }

  // eslint-disable-next-line class-methods-use-this
  async updateEligibleClasses(groupId: string, employeeClasses: EeClass[], periodId: string): Promise<void> {
    await apolloClient.mutate({
      mutation: UPDATE_EMPLOYEE_CLASSES,
      variables: { groupId, employeeClasses, periodId },
    });
  }

  // eslint-disable-next-line class-methods-use-this
  async updateProductClasses(
    groupId: string,
    productClassesMapping: ProductsInClass[],
    periodId: string,
  ): Promise<void> {
    await apolloClient.mutate({
      mutation: UPDATE_PRODUCT_CLASSES,
      variables: { groupId, productClassesMapping, periodId },
    });
  }

  // eslint-disable-next-line class-methods-use-this
  async checkElection(
    groupId: string,
    periodId: string,
  ): Promise<ApolloQueryResult<{ getSubsValidations: CompleteElectionsCheck }>> {
    return apolloClient.query<{ getSubsValidations: CompleteElectionsCheck }>({
      query: GET_SUBS_VALIDATIONS,
      fetchPolicy: 'no-cache',
      variables: { groupId, periodId },
    });
  }

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
